"use client";

import { useEffect, useLayoutEffect, useRef, useState, type ComponentType, type FormEvent } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { buttonClasses } from "@/lib/ui/buttonClasses";
import { SiteNav } from "@/components/marketing/site-nav";
import { SiteFooter } from "@/components/marketing/site-footer";
import { Users, Calendar, MessageSquare, X, Sparkles, Zap, ArrowRight, Rocket, Trophy, Compass, CheckCircle2, PenTool, MapPin, Flame, TrendingUp, Search } from "lucide-react";
import { cn, isCapacitorApp } from "@/lib/utils";
import { OfflineBanner } from "@/components/offline/offline-banner";
import { Shine } from "@/components/ui/shine";
import { useTranslations } from "@/hooks/use-translations";
import { MatchLadder } from "@/components/marketing/match-ladder";
import { LayoutProbe } from "@/components/marketing/layout-probe";
import { DownloadAppCTA } from "./download-app-cta";
import { apiUrl } from "@/lib/api-base";

// The hero chips cycle through real capabilities so the preview teaches something new
// on every beat instead of restating the same two proof points. Each beat pairs one
// left and one right chip, and no capability appears twice on screen at once.
type PreviewProof = {
  icon: ComponentType<{ className?: string }>;
  tone: "primary" | "amber";
  titleKey: string;
  metaKey: string;
};

type PreviewMember = {
  icon: ComponentType<{ className?: string }>;
  roleKey: string;
  metaKey: string;
  bioKey: string;
  tagKeys: string[];
};

const previewMembers: PreviewMember[] = [
  {
    icon: Rocket,
    roleKey: "preview.role",
    metaKey: "preview.roleMeta",
    bioKey: "preview.bio",
    tagKeys: ["preview.tag1", "preview.tag2", "preview.tag3"],
  },
  {
    icon: PenTool,
    roleKey: "preview.role2",
    metaKey: "preview.roleMeta2",
    bioKey: "preview.bio2",
    tagKeys: ["preview.tag2a", "preview.tag2b", "preview.tag2c"],
  },
  {
    icon: Compass,
    roleKey: "preview.role3",
    metaKey: "preview.roleMeta3",
    bioKey: "preview.bio3",
    tagKeys: ["preview.tag3a", "preview.tag3b", "preview.tag3c"],
  },
];

const previewBeats: { left: PreviewProof; right: PreviewProof }[] = [
  {
    left: { icon: Calendar, tone: "primary", titleKey: "preview.eventName", metaKey: "preview.eventMeta" },
    right: { icon: Zap, tone: "amber", titleKey: "preview.streak", metaKey: "preview.streakMeta" },
  },
  {
    left: { icon: Compass, tone: "primary", titleKey: "nav.discover", metaKey: "preview.matchesMeta" },
    right: { icon: Users, tone: "primary", titleKey: "nav.communities", metaKey: "preview.community" },
  },
  {
    left: { icon: TrendingUp, tone: "primary", titleKey: "preview.opportunity", metaKey: "preview.opportunityMeta" },
    right: { icon: Trophy, tone: "amber", titleKey: "steps.profile.title", metaKey: "preview.roleMeta" },
  },
];

/* Each of these now has a page of its own, so the card goes there rather than being a
   dead end that says three sentences and stops. */
const personas = [
  {
    icon: Rocket,
    titleKey: "personas.founders.title",
    descriptionKey: "personas.founders.description",
    href: "/for/founders",
  },
  {
    icon: Compass,
    titleKey: "personas.owners.title",
    descriptionKey: "personas.owners.description",
    href: "/for/business-owners",
  },
];

const steps = [
  {
    titleKey: "steps.profile.title",
    descriptionKey: "steps.profile.description",
  },
  {
    titleKey: "nav.discover",
    descriptionKey: "ladder.stages.everyone.note",
  },
  {
    titleKey: "steps.grow.title",
    descriptionKey: "steps.grow.description",
  },
];

/**
 * Renders a translated string that marks one phrase for the gradient highlight, e.g.
 * "…a network that <accent>compounds.</accent>".
 *
 * The highlight has to be able to sit anywhere in the sentence, because word order moves when
 * the sentence is translated: German pushes the verb to the end, Spanish puts the adjective
 * after the noun. Splitting this into two keys, a first half and an accent word, would hand
 * every translator two fragments and produce word order no native speaker would write.
 *
 * A locale that drops the tag simply renders as plain text rather than breaking.
 */
function withAccent(text: string) {
  return text.split(/<accent>|<\/accent>/).map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className="gradient-primary bg-clip-text text-transparent">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

/* Sixty one stills pulled from one continuous five second shot. Scroll picks the frame,
   so the camera moves at exactly the speed the reader moves and stops when they stop.
   854KB for the whole sequence, which is lighter than the video it came from. */


function PreviewProofChip({ proof, active }: { proof: PreviewProof; active: boolean }) {
  const { t } = useTranslations("landing");
  const Icon = proof.icon;
  const amber = proof.tone === "amber";

  return (
    <span
      data-active={active ? "true" : "false"}
      className="gs-proof-beat col-start-1 row-start-1 flex items-center gap-2.5"
    >
      <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", amber ? "bg-amber-100" : "bg-primary/10")}>
        <Icon className={cn("h-5 w-5", amber ? "text-amber-500" : "text-primary")} />
      </span>
      <span className="leading-tight">
        <span className="block whitespace-nowrap text-xs font-bold text-neutral-900">{t(proof.titleKey)}</span>
        <span className="block whitespace-nowrap text-[11px] font-medium text-neutral-500">{t(proof.metaKey)}</span>
      </span>
    </span>
  );
}

export default function LandingHomeClient() {
  const { t } = useTranslations("landing");
  const router = useRouter();
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistStatus, setWaitlistStatus] = useState<"idle" | "submitting" | "success" | "already" | "error">("idle");
  const [waitlistError, setWaitlistError] = useState("");
  const [previewBeat, setPreviewBeat] = useState(0);
  const [leavingBeat, setLeavingBeat] = useState<number | null>(null);

  /* Safety net for the phone app.
     The app should never reach this page: middleware rewrites "/" to the app's own
     one-screen welcome for native requests. It can only land here on the very first
     launch of an iOS binary older than 2026-08-23, which carries no "Capacitor" marker
     in its User-Agent and has not yet written the gs_shell cookie, so the server had
     nothing to go on. NativeShellCookie writes that cookie on this same mount, so this
     happens once per install at most and every later launch is decided server side.
     See lib/native-shell.ts. */
  useEffect(() => {
    if (isCapacitorApp()) router.replace("/welcome");
  }, [router]);

  /* The preview rotates on its own, vertically, and loops. It used to advance on scroll
     and swipe sideways, which broke in Safari: three stacked, transformed, backdrop-blurred
     cards left stale layers behind, so the card either painted twice on top of itself or
     vanished entirely on the way back up the page. Only one card is in flow at a time now,
     with the outgoing one overlaid for the length of the transition, so there is never a
     stack of live layers to go wrong. */
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    let timer: number | undefined;

    const sync = () => {
      window.clearInterval(timer);
      if (reduce.matches) {
        setPreviewBeat(0);
        setLeavingBeat(null);
        return;
      }
      timer = window.setInterval(() => {
        setPreviewBeat((beat) => {
          setLeavingBeat(beat);
          return (beat + 1) % previewMembers.length;
        });
      }, 3800);
    };

    sync();
    reduce.addEventListener("change", sync);
    return () => {
      window.clearInterval(timer);
      reduce.removeEventListener("change", sync);
    };
  }, []);

  /* The outgoing card only exists while it is animating out. */
  useEffect(() => {
    if (leavingBeat === null) return;
    const t = window.setTimeout(() => setLeavingBeat(null), 700);
    return () => window.clearTimeout(t);
  }, [leavingBeat]);

  const [overlayLayout, setOverlayLayout] = useState({ height: "100vh", top: "0px" });
  const scrollLockRef = useRef(0);

  // useLayoutEffect: sync visual viewport before paint so the first frame matches the keyboard-safe area.
  useLayoutEffect(() => {
    if (!showWaitlistModal) return;

    scrollLockRef.current = window.scrollY;
    const savedY = scrollLockRef.current;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${savedY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";

    const vv = window.visualViewport;
    const sync = () => {
      if (vv) {
        setOverlayLayout({
          height: `${vv.height}px`,
          top: `${vv.offsetTop}px`,
        });
      } else {
        setOverlayLayout({
          height: `${window.innerHeight}px`,
          top: "0px",
        });
      }
    };

    sync();
    if (vv) {
      vv.addEventListener("resize", sync);
      vv.addEventListener("scroll", sync);
    } else {
      window.addEventListener("resize", sync);
    }

    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      window.scrollTo(0, savedY);
      if (vv) {
        vv.removeEventListener("resize", sync);
        vv.removeEventListener("scroll", sync);
      } else {
        window.removeEventListener("resize", sync);
      }
    };
  }, [showWaitlistModal]);

  async function handleWaitlistSubmit(e: FormEvent) {
    e.preventDefault();
    const email = waitlistEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setWaitlistError(t("waitlist.invalidEmail"));
      setWaitlistStatus("error");
      return;
    }
    setWaitlistStatus("submitting");
    setWaitlistError("");
    try {
      const res = await fetch(apiUrl("/api/waitlist"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || t("waitlist.genericError"));
      }
      setWaitlistStatus(data.alreadyOnList ? "already" : "success");
    } catch (err: unknown) {
      setWaitlistError(err instanceof Error ? err.message : t("waitlist.genericError"));
      setWaitlistStatus("error");
    }
  }

  return (
    <>
    {/* Plain document scroll, on every device.
        This page used to pin itself to the viewport height and scroll an inner
        container instead, for anything reporting Android plus Chrome-in-Capacitor. That
        existed to work around a touch-scroll freeze in the Android WebView, and it is no
        longer reachable: the app never sees this page, because middleware rewrites "/"
        to /welcome for native requests (src/lib/native-shell.ts).

        It was never only the app, either. isAndroid() falls back to a User-Agent test, so
        every Android visitor on the plain website was getting the WebView workaround too:
        a fixed-height flex column, a nested scroller, and touch-action pinned to pan-y,
        instead of the document scroll that iOS and desktop have always used here. */}
    <div data-consent-inset className="min-h-screen bg-[#f6f8fc]">
      <OfflineBanner />
      <SiteNav />

      <div>
      <main data-fluid-layout className="flex w-full flex-col">
        {/* Hero Section */}
        <section
          className="relative overflow-hidden pb-24 md:pb-32 lg:pb-24"
          style={{ paddingTop: "0.75rem" }}
        >
          {/* Ambient glow — a soft, wide wash (no hard edge, no pulse) so it reads as light, not a shape */}
          {/* The same soft wash, painted rather than filtered. It was a hard edged circle
              with blur(130px) over it, and a blur of that radius makes the compositor
              allocate an offscreen buffer several hundred pixels larger than the element
              on every side: roughly 1460 by 1200 points, which on a three times phone is
              about 60MB of GPU buffer for a wash at six percent opacity. A radial
              gradient is the same picture for the cost of a paint. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-64 left-1/2 z-0 h-[980px] w-[1240px] -translate-x-1/2 md:h-[1200px] md:w-[1560px]"
            style={{
              background:
                "radial-gradient(closest-side, rgba(11,99,224,0.06) 0%, rgba(11,99,224,0.042) 42%, rgba(11,99,224,0.014) 72%, rgba(11,99,224,0) 100%)",
            }}
          ></div>

          <div className="gs-page relative z-10 grid w-full items-center gap-10 pt-4 md:gap-12 md:pt-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10">
            {/* Left: copy */}
            <div className="text-center lg:text-left">
              <span className="mx-auto mb-4 inline-flex w-fit items-center gap-2 rounded-full gradient-primary px-3.5 py-1.5 text-[12px] font-semibold tracking-wide text-white shadow-[0_2px_12px_rgba(11, 99, 224,0.35)] md:text-[13px] lg:mx-0">
                <Sparkles className="h-3.5 w-3.5" />
                {t("hero.badge")}
              </span>
              <h1 className="gs-display gs-display-hero mb-4 text-neutral-900 md:mb-6">
                {/*
                  One key, not two. The highlighted word has to move inside the sentence when the
                  sentence is translated: in German the verb lands at the end, in Spanish the
                  adjective follows the noun. Splitting this into "first half" and "accent word"
                  would hand translators two fragments and produce word order no native speaker
                  would write. The <accent> tag travels with the meaning instead.
                */}
                {withAccent(t("hero.headline"))}
              </h1>
              <p className="mx-auto mb-5 max-w-xl text-[15px] leading-relaxed text-neutral-700 md:mb-6 md:text-lg lg:mx-0">
                {t("why.body")}
              </p>
              <div className="flex flex-col items-center gap-3 sm:flex-row sm:flex-wrap sm:justify-center lg:justify-start">
                <Link
                  href="/auth/signup"
                  className={buttonClasses({ size: "lg", className: "group w-full sm:w-auto lg:h-16 lg:px-9 lg:text-lg lg:rounded-2xl shadow-[0_8px_24px_rgba(11, 99, 224,0.4)] hover:shadow-[0_10px_30px_rgba(11, 99, 224,0.5)]" })}
                >
                  {t("hero.ctaPrimary")}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <button
                  type="button"
                  onClick={() => setShowWaitlistModal(true)}
                  className="inline-flex min-h-11 items-center text-sm font-semibold text-neutral-600 underline-offset-4 transition-colors hover:text-primary hover:underline"
                >
                  {t("hero.ctaSecondary")}
                </button>
              </div>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-neutral-500 lg:justify-start">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> {t("hero.trustFree")}</span>
              </div>
            </div>

            {/* Right: mock app UI */}
            <div className="relative mx-auto mt-6 w-full max-w-md sm:mt-8 lg:mt-0 lg:max-w-none">
              {/* One card holds the layout; the outgoing one is overlaid on top of it and
                  only exists for the length of its animation. Two painted layers at most,
                  and only for 700ms, which is what keeps Safari honest here. */}
              <div className="relative">
                {previewMembers.map((member, index) => {
                  const isActive = index === previewBeat;
                  const isLeaving = index === leavingBeat && leavingBeat !== previewBeat;
                  if (!isActive && !isLeaving) return null;
                  const MemberIcon = member.icon;

                  return (
                    <div
                      key={`${member.roleKey}-${isLeaving ? "out" : "in"}`}
                      aria-hidden={isLeaving ? "true" : undefined}
                      className={cn(
                        "rounded-3xl border border-neutral-200/80 bg-white p-5 shadow-[0_24px_60px_-20px_rgba(11,99,224,0.35)] md:p-6",
                        isLeaving
                          ? "pointer-events-none absolute inset-x-0 top-0 animate-[gs-card-out_700ms_cubic-bezier(0.4,0,0.2,1)_forwards]"
                          : "relative animate-[gs-card-in_700ms_cubic-bezier(0.16,1,0.3,1)_both]",
                      )}
                    >
                      <span className="absolute end-4 top-4 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">{t("preview.exampleTag")}</span>
                      <div className="flex items-start gap-4">
                        <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl gradient-primary text-white shadow-lg">
                          <MemberIcon className="h-6 w-6" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="block text-base font-bold text-neutral-900">{t(member.roleKey)}</span>
                          <span className="block text-sm font-medium text-neutral-500">{t(member.metaKey)}</span>
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {member.tagKeys.map((tagKey) => (
                          <span key={tagKey} className="rounded-full bg-primary/8 px-3 py-1 text-xs font-semibold text-primary">{t(tagKey)}</span>
                        ))}
                      </div>
                      {/* A fixed minimum keeps the card the same height as the bios change,
                          so the chips pinned to its corners do not jump between beats. */}
                      <div className="mt-4 min-h-[5.5rem] rounded-2xl bg-neutral-50 p-3.5 text-sm leading-relaxed text-neutral-600">
                        {t(member.bioKey)}
                      </div>
                      <div className="mt-4 grid grid-cols-2 gap-2.5">
                        <span className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-neutral-200 bg-white py-2.5 text-sm font-semibold text-neutral-700">
                          <MessageSquare className="h-4 w-4" /> {t("preview.message")}
                        </span>
                        <span className="inline-flex items-center justify-center gap-1.5 rounded-xl gradient-primary py-2.5 text-sm font-bold text-white">
                          {t("preview.connect")}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Each beat is rendered into the same grid cell so a chip crossfades in place:
                  it never remounts (the float keeps running) and never resizes between beats or
                  locales. Hidden from assistive technology because the profile card beside it
                  already carries the real meaning. */}
              <div
                aria-hidden="true"
                className="gs-preview-chip absolute z-20 -top-8 left-1 grid animate-[float_6s_ease-in-out_infinite] rounded-2xl border border-neutral-200/80 bg-white/95 px-3 py-2 shadow-xl sm:-left-4 sm:-top-9 sm:px-3.5 sm:py-2.5"
                style={{ animationDelay: "0.3s" }}
              >
                {previewBeats.map((beat, index) => (
                  <PreviewProofChip key={index} proof={beat.left} active={index === previewBeat} />
                ))}
              </div>

              <div
                aria-hidden="true"
                className="gs-preview-chip absolute z-20 -bottom-8 right-1 grid animate-[float_7s_ease-in-out_infinite] rounded-2xl border border-neutral-200/80 bg-white/95 px-3 py-2 shadow-xl sm:-bottom-9 sm:-right-4 sm:px-3.5 sm:py-2.5"
                style={{ animationDelay: "1.4s" }}
              >
                {previewBeats.map((beat, index) => (
                  <PreviewProofChip key={index} proof={beat.right} active={index === previewBeat} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* The match ladder: the page's owned mechanic. The page holds and the wall of
            members narrows from everyone down to the one both people said yes to. It sits
            straight after the hero because it IS the product's argument. */}
        <MatchLadder />

        {/* Who it's for */}
        <section className="py-20 md:py-28">
          <div className="gs-page">
            <div className="mb-9 text-center md:mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary">{t("whoFor.eyebrow")}</span>
              <h2 className="gs-headline gs-measure mx-auto mt-3 text-neutral-900">
                {t("whoFor.heading")}
              </h2>
            </div>
            <div className="mx-auto grid max-w-4xl grid-cols-1 gap-5 sm:grid-cols-2">
              {personas.map((p, index) => {
                const Icon = p.icon;
                return (
                  <Link
                    key={index}
                    href={p.href}
                    className="group relative overflow-hidden rounded-3xl border border-neutral-200 bg-white p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-[0_18px_40px_-16px_rgba(11, 99, 224,0.3)]"
                  >
                    <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h3 className="mb-2 text-xl font-bold text-neutral-900">{t(p.titleKey)}</h3>
                    <p className="text-[15px] leading-relaxed text-neutral-600">{t(p.descriptionKey)}</p>
                    <ArrowRight
                      aria-hidden
                      className="absolute right-6 top-7 h-4 w-4 text-neutral-300 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:text-primary group-hover:opacity-100"
                    />
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* The film. A single unbroken push through a busy event that lands on two
            people talking. Scroll drives the camera, so it moves at the reader's pace,
            stops when they stop and can be scrubbed back. It sits here because it is
            the bridge from who this is for into why it exists. */}
        {/* This was a scroll-driven film: 61 frames decoded and held, and a canvas
            repainted on every scroll frame, across three viewports of scrolling. On a
            phone that was sustained work on the longest page in the product, and it is
            what made the app run hot. A still says the same thing for 112KB and one
            decode. Same full-bleed treatment as the events band lower down. */}
        <section className="relative overflow-hidden">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/images/one-conversation.webp"
            /* A phone holds the DECODED bitmap, and a bitmap is sized by its pixels, not
               by its file size: this photograph is 131KB over the wire and 9MB in memory,
               and there are two of them on this page. Phones now get a 1400 wide copy
               (4.2MB) and desktops keep the full one. Under the navy scrim the difference
               is not visible; nine megabytes each on a handset is. */
            srcSet="/images/one-conversation-1400.webp 1400w, /images/one-conversation.webp 2048w"
            sizes="100vw"
            alt={t("film.alt")}
            loading="lazy"
            decoding="async"
            className="h-[420px] w-full object-cover md:h-[540px]"
          />
          <div
            className="absolute inset-0"
            aria-hidden="true"
            style={{ background: "linear-gradient(to top, rgba(9,15,57,0.92) 0%, rgba(9,15,57,0.45) 50%, rgba(9,15,57,0.15) 100%)" }}
          />
          <div className="absolute inset-x-0 bottom-0 pb-10 md:pb-14">
            <div className="gs-page">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-white">
                {t("film.eyebrow")}
              </span>
              <p className="gs-headline mt-3 max-w-[20ch] text-white">{t("ladder.stages.theOne.note")}</p>
              <p className="gs-lede mt-5 max-w-[48ch] text-white/80">{t("ladder.stages.shortlist.note")}</p>
            </div>
          </div>
        </section>

        {/* Why GrowthSync — lead-in to the feature sections */}
        <section className="border-t border-neutral-200 pt-16 pb-4 text-center md:pt-20 md:pb-6">
          <div className="gs-page">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary">{t("why.eyebrow")}</span>
            <h2 className="gs-headline gs-measure mx-auto mt-3 text-neutral-900">
              {t("why.heading")}
            </h2>
            <p className="gs-lede gs-measure mx-auto mt-5 text-neutral-600">
              {t("why.body")}
            </p>
          </div>
        </section>

        {/* Feature: Discover */}
        <section id="discover" className="scroll-mt-28 pt-12 pb-20 md:pt-16 md:pb-28">
          <div className="gs-page grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-primary"><Search className="h-4 w-4" /> {t("discover.eyebrow")}</span>
              <h2 className="gs-headline mt-3 text-neutral-900">
                {t("nav.discover")}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-neutral-600 md:text-lg">
                {t("ladder.stages.everyone.note")}
              </p>
              <ul className="mt-6 space-y-3">
                {["Filter by goal, stage, skills and location", "Context-rich profiles, no cold DMs"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-[15px] text-neutral-700"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><span>{t}</span></li>
                ))}
              </ul>
            </div>
            {/* mock: discover list */}
            <div className="relative lg:order-2">
              <div className="rounded-3xl border border-neutral-200 bg-white p-5 shadow-[0_24px_60px_-24px_rgba(11, 99, 224,0.3)]">
                <div className="mb-3 flex justify-end"><span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">{t("preview.exampleTag")}</span></div>
                <div className="mb-4 flex items-center gap-2 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm text-neutral-500">
                  <Search className="h-4 w-4" /> {t("discover.searchExample")}
                </div>
                <div className="mb-4 flex flex-wrap gap-2">
                  {["Founders", "London", "Fundraising"].map((c) => (
                    <span key={c} className="inline-flex items-center gap-1 rounded-full bg-primary/8 px-2.5 py-1 text-xs font-semibold text-primary">{c}<X className="h-3 w-3" /></span>
                  ))}
                </div>
                <div className="space-y-2.5">
                  {[{ role: "Business owner", meta: "Growth · Fintech · Manchester", icon: Compass }, { role: "Founder", meta: "Seed stage · SaaS · Remote", icon: Rocket }].map((m) => {
                    const I = m.icon;
                    return (
                      <div key={m.role} className="flex items-center gap-3 rounded-2xl border border-neutral-200 p-3">
                        <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl gradient-primary text-white">
                          <I className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="block text-sm font-bold text-neutral-900">{m.role}</span>
                          <span className="block text-xs text-neutral-500">{m.meta}</span>
                        </div>
                        <span className="rounded-lg gradient-primary px-3 py-1.5 text-xs font-bold text-white">Connect</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Feature: Events */}
        <section id="events" className="scroll-mt-28 border-y border-neutral-200 bg-white py-20 md:py-28">
          <div className="gs-page grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div className="lg:order-2">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-primary"><Calendar className="h-4 w-4" /> {t("events.eyebrow")}</span>
              <h2 className="gs-headline mt-3 text-neutral-900">
                {t("events.heading")}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-neutral-600 md:text-lg">
                {t("events.body")}
              </p>
              <ul className="mt-6 space-y-3">
                {["Member-hosted salons, workshops & roundtables", "See who's going before you commit", "Online and in real life"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-[15px] text-neutral-700"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><span>{t}</span></li>
                ))}
              </ul>
            </div>
            {/* mock: event card */}
            <div className="relative lg:order-1">
              <div className="rounded-3xl border border-neutral-200 bg-white p-5 shadow-[0_24px_60px_-24px_rgba(11, 99, 224,0.3)]">
                <div className="mb-3 flex justify-end"><span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">{t("preview.exampleTag")}</span></div>
                <div className="flex items-start gap-4">
                  <div className="flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-2xl gradient-primary text-white">
                    <span className="text-[10px] font-bold uppercase tracking-wider opacity-90">Jun</span>
                    <span className="text-2xl font-extrabold leading-none">12</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="block text-lg font-bold text-neutral-900">Founder Salon</span>
                    <span className="mt-1 flex items-center gap-1.5 text-sm text-neutral-500"><Calendar className="h-4 w-4" /> {t("events.exampleWhen")}</span>
                    <span className="mt-0.5 flex items-center gap-1.5 text-sm text-neutral-500"><MapPin className="h-4 w-4" /> {t("events.exampleWhere")}</span>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between rounded-2xl bg-neutral-50 p-3">
                  <div className="flex -space-x-2">
                    {[Rocket, Compass, PenTool].map((I, i) => (
                      <span key={i} className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white gradient-primary text-white"><I className="h-3.5 w-3.5" /></span>
                    ))}
                    <span className="flex h-8 items-center rounded-full border-2 border-white bg-neutral-200 px-2 text-xs font-bold text-neutral-600">+12</span>
                  </div>
                  <span className="flex items-center gap-1 text-xs font-semibold text-neutral-500"><Users className="h-4 w-4" /> {t("events.exampleGoing")}</span>
                </div>
                <span className="relative mt-4 flex items-center justify-center gap-1.5 overflow-hidden rounded-xl gradient-primary py-3 text-sm font-bold text-white">{t("events.exampleCta")}<Shine duration="3s" /></span>
              </div>
            </div>
          </div>
        </section>

        {/* A real room, full bleed. Deliberately NOT the world the film lives in: the film
            and this band were both dim tungsten evenings, so the page showed the same
            photograph twice and the second one read as filler. Daylight, high key and cool
            gives the lower half of the page its own moment. */}
        <section className="relative overflow-hidden">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/images/events-daylight.webp"
            /* A phone holds the DECODED bitmap, and a bitmap is sized by its pixels, not
               by its file size: this photograph is 131KB over the wire and 9MB in memory,
               and there are two of them on this page. Phones now get a 1400 wide copy
               (4.2MB) and desktops keep the full one. Under the navy scrim the difference
               is not visible; nine megabytes each on a handset is. */
            srcSet="/images/events-daylight-1400.webp 1400w, /images/events-daylight.webp 2048w"
            sizes="100vw"
            alt={t("eventsBand.alt")}
            loading="lazy"
            decoding="async"
            className="h-[420px] w-full object-cover md:h-[540px]"
          />
          <div
            className="absolute inset-0"
            aria-hidden="true"
            style={{ background: "linear-gradient(to top, rgba(9,15,57,0.88) 0%, rgba(9,15,57,0.35) 45%, rgba(9,15,57,0.15) 100%)" }}
          />
          <div className="absolute inset-x-0 bottom-0 pb-10 md:pb-14">
            <div className="gs-page">
              <p className="gs-headline max-w-[24ch] text-white">{t("events.heading")}</p>
            </div>
          </div>
        </section>

        {/* Feature: Communities */}
        <section id="communities" className="scroll-mt-28 py-20 md:py-28">
          <div className="gs-page grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-primary"><MessageSquare className="h-4 w-4" /> {t("communities.eyebrow")}</span>
              <h2 className="gs-headline mt-3 text-neutral-900">
                {t("nav.communities")}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-neutral-600 md:text-lg">
                {t("communities.body")}
              </p>
              <ul className="mt-6 space-y-3">
                {["Topic spaces for fundraising, GTM, hiring & more"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-[15px] text-neutral-700"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><span>{t}</span></li>
                ))}
              </ul>
            </div>
            {/* mock: community thread */}
            <div className="relative lg:order-2">
              <div className="rounded-3xl border border-neutral-200 bg-white p-5 shadow-[0_24px_60px_-24px_rgba(11, 99, 224,0.3)]">
                <div className="mb-3 flex justify-end"><span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">{t("preview.exampleTag")}</span></div>
                <div className="mb-4 flex items-center gap-2 border-b border-neutral-100 pb-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-sm font-bold text-primary">#</span>
                  <span className="text-sm font-bold text-neutral-900">{t("communities.exampleTopic")}</span>
                  <span className="ml-auto text-xs text-neutral-500">{t("nav.communities")}</span>
                </div>
                <div className="rounded-2xl bg-neutral-50 p-5">
                  <MessageSquare className="mb-3 h-7 w-7 text-primary" />
                  <p className="text-sm font-medium leading-relaxed text-neutral-700">{t("communities.examplePrompt")}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Feature: Momentum */}
        <section id="momentum" className="scroll-mt-28 border-y border-neutral-200 bg-white py-20 md:py-28">
          <div className="gs-page grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div className="lg:order-2">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-primary"><Trophy className="h-4 w-4" /> {t("momentum.eyebrow")}</span>
              <h2 className="gs-headline mt-3 text-neutral-900">
                {t("momentum.heading")}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-neutral-600 md:text-lg">
                {t("momentum.body")}
              </p>
              <ul className="mt-6 space-y-3">
                {["Earn XP for real contribution, not vanity", "Daily streaks keep you consistent", "Climb the leaderboard with the community"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-[15px] text-neutral-700"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><span>{t}</span></li>
                ))}
              </ul>
            </div>
            {/* mock: momentum / leaderboard */}
            <div className="relative lg:order-1">
              <div className="rounded-3xl border border-neutral-200 bg-white p-5 shadow-[0_24px_60px_-24px_rgba(11, 99, 224,0.3)]">
                <div className="mb-3 flex justify-end"><span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">{t("preview.exampleTag")}</span></div>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-sm font-bold text-neutral-900"><Flame className="h-5 w-5 text-amber-500" /> 7-day streak</span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700"><Zap className="h-3.5 w-3.5" /> +120 XP</span>
                </div>
                <div className="mt-4">
                  <div className="mb-1.5 flex items-center justify-between text-xs font-semibold text-neutral-500"><span className="inline-flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5 text-primary" /> {t("momentum.exampleLevel")}</span><span>1,240 / 1,500 XP</span></div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-neutral-100"><div className="h-full w-[82%] rounded-full gradient-primary" /></div>
                </div>
                <div className="mt-5 space-y-2">
                  {[{ rank: 1, role: "Founder", icon: Rocket, xp: "2,480", top: true }, { rank: 2, role: "Business owner", icon: Compass, xp: "1,910" }, { rank: 3, role: "Founder", icon: Rocket, xp: "1,240" }].map((r) => {
                    const I = r.icon;
                    return (
                      <div key={r.rank} className={cn("flex items-center gap-3 rounded-2xl border p-2.5", r.top ? "border-primary/30 bg-primary/5" : "border-neutral-200")}>
                        <span className={cn("flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold", r.top ? "gradient-primary text-white" : "bg-neutral-100 text-neutral-500")}>{r.rank}</span>
                        <span className="flex h-8 w-8 items-center justify-center rounded-full gradient-primary text-white"><I className="h-4 w-4" /></span>
                        <span className="flex-1 text-sm font-semibold text-neutral-800">{r.role}</span>
                        <span className="text-sm font-bold text-neutral-900">{r.xp} XP</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-20 md:py-28">
          <div className="gs-page">
            <div className="mb-10 text-center md:mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary">{t("howItWorks.eyebrow")}</span>
              <h2 className="gs-headline mt-3 text-neutral-900">{t("howItWorks.eyebrow")}</h2>
            </div>
            {/* Three identical boxes with 1, 2, 3 in them is the most forgettable way to
                show a sequence, and it reads as filler. This is one continuous line with
                the steps hung off it, so the eye follows the order instead of scanning
                three equal blocks: horizontal on a wide screen, vertical on a phone. */}
            <ol className="gs-steps" role="list">
              {steps.map((step) => (
                <li key={step.titleKey} className="gs-step">
                  <span className="gs-step-node" aria-hidden="true" />
                  <h3 className="text-lg font-bold text-neutral-900">{t(step.titleKey)}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-neutral-600">{t(step.descriptionKey)}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Download the app */}
        <DownloadAppCTA />

        {/* Final CTA */}
        <section className="pb-24 md:pb-32">
          <div className="relative gs-page mx-auto max-w-[calc(100%-1.5rem)] overflow-hidden rounded-[2rem] gradient-primary py-12 text-center shadow-[0_30px_80px_-24px_rgba(11, 99, 224,0.6)] sm:max-w-none md:py-16">
            <Shine duration="4s" className="via-white/20" />
            <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-10 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
            <div className="relative">
              <h2 className="gs-headline mx-auto max-w-[24ch] text-white">
                {t("closing.heading")}
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white md:text-lg">
                {t("closing.body")}
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Link
                  href="/auth/signup"
                  className="group inline-flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-white px-8 text-lg font-bold text-primary shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl sm:w-auto"
                >
                  Join GrowthSync free
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <Link href="/auth/login" className="inline-flex min-h-11 items-center text-sm font-semibold !text-white underline-offset-4 hover:underline">
                  {t("closing.haveAccount")}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />

      <LayoutProbe />
      </div>

    </div>

      {/* Waitlist Modal — portalled to body to escape flex/overflow ancestors */}
      {showWaitlistModal && createPortal(
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
            }}
            onClick={() => setShowWaitlistModal(false)}
          />
          <div
            className={cn(
              "waitlist-modal-overlay flex min-h-0 flex-col items-center justify-start px-4",
              "pt-[max(2.75rem,env(safe-area-inset-top,0px))] pb-4",
              "md:justify-center md:px-6 md:py-8 md:pb-8"
            )}
            style={{
              position: "fixed",
              left: 0,
              top: overlayLayout.top,
              width: "100vw",
              height: overlayLayout.height,
              zIndex: 10000,
              overflowY: "auto",
              overflowX: "hidden",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              pointerEvents: "auto",
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowWaitlistModal(false);
            }}
          >
          <div
            className="waitlist-modal-card max-h-[min(560px,calc(100%-1rem))] shrink"
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "420px",
              margin: "0 auto 16px",
              padding: "32px",
              backgroundColor: "#fff",
              borderRadius: "20px",
              boxShadow: "0 32px 64px -16px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.04)",
              boxSizing: "border-box",
              animation: "waitlistModalIn 0.2s ease-out",
            }}
          >
            <button
              type="button"
              onClick={() => setShowWaitlistModal(false)}
              style={{
                position: "absolute",
                right: "16px",
                top: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                border: "none",
                backgroundColor: "transparent",
                color: "#9ca3af",
                cursor: "pointer",
                transition: "background-color 0.15s, color 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#f3f4f6"; e.currentTarget.style.color = "#4b5563"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#9ca3af"; }}
              aria-label="Close"
            >
              <X style={{ width: "18px", height: "18px" }} />
            </button>

            {waitlistStatus === "success" || waitlistStatus === "already" ? (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "linear-gradient(135deg, #217af6, #4a5efd)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                </div>
                <p style={{ fontSize: "18px", fontWeight: 600, color: "#0f172a", margin: "0 0 8px", maxWidth: "100%" }}>
                  {waitlistStatus === "already" ? "You\u2019re already on the list!" : "You\u2019re on the list!"}
                </p>
                <p style={{ fontSize: "14px", color: "#64748b", margin: 0, maxWidth: "100%" }}>
                  {waitlistStatus === "already"
                    ? "We already have your email \u2014 no need to sign up again. We\u2019ll be in touch soon."
                    : "We\u2019ll let you know when it\u2019s time."}
                </p>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: "24px" }}>
                  <div style={{ width: "40px", height: "40px", borderRadius: "12px", background: "linear-gradient(135deg, #e4effe, #217af620)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-vivid)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></svg>
                  </div>
                  <h2 style={{ fontSize: "22px", fontWeight: 700, color: "#0f172a", margin: "0 0 8px", letterSpacing: "-0.01em", lineHeight: 1.2, maxWidth: "100%" }}>
                    {t("waitlist.title")}
                  </h2>
                  <p style={{ fontSize: "15px", color: "#64748b", margin: 0, lineHeight: 1.5, maxWidth: "100%" }}>
                    {t("waitlist.subtitle")}
                  </p>
                </div>
                <form onSubmit={handleWaitlistSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div>
                    <label htmlFor="waitlist-email" style={{ display: "block", fontSize: "13px", fontWeight: 500, color: "#334155", marginBottom: "6px" }}>{t("waitlist.emailLabel")}</label>
                    <input
                      id="waitlist-email"
                      type="email"
                      value={waitlistEmail}
                      onChange={(e) => { setWaitlistEmail(e.target.value); if (waitlistStatus === "error") setWaitlistStatus("idle"); }}
                      placeholder="you@example.com"
                      required
                      autoFocus
                      style={{
                        width: "100%",
                        height: "48px",
                        borderRadius: "12px",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#f8fafc",
                        padding: "0 16px",
                        fontSize: "15px",
                        color: "#0f172a",
                        outline: "none",
                        boxSizing: "border-box",
                        transition: "border-color 0.15s, box-shadow 0.15s, background-color 0.15s",
                      }}
                      onFocus={(e) => {
                        const input = e.currentTarget;
                        input.style.borderColor = "var(--color-primary-vivid)";
                        input.style.boxShadow = "0 0 0 3px rgba(11, 99, 224, 0.12)";
                        input.style.backgroundColor = "#fff";
                        requestAnimationFrame(() => {
                          const el = document.getElementById("waitlist-email");
                          if (el?.isConnected) {
                            el.scrollIntoView({ block: "nearest", behavior: "smooth" });
                          }
                        });
                      }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.backgroundColor = "#f8fafc"; }}
                    />
                  </div>
                  {waitlistStatus === "error" && waitlistError && (
                    <p style={{ fontSize: "13px", color: "#dc2626", margin: 0, maxWidth: "100%" }}>{waitlistError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={waitlistStatus === "submitting"}
                    style={{
                      position: "relative",
                      overflow: "hidden",
                      width: "100%",
                      height: "48px",
                      borderRadius: "12px",
                      border: "none",
                      background: "linear-gradient(135deg, #217af6, #4a5efd)",
                      color: "#fff",
                      fontSize: "15px",
                      fontWeight: 600,
                      cursor: waitlistStatus === "submitting" ? "not-allowed" : "pointer",
                      opacity: waitlistStatus === "submitting" ? 0.7 : 1,
                      transition: "opacity 0.15s, transform 0.15s, box-shadow 0.15s",
                      boxShadow: "0 4px 12px rgba(11, 99, 224, 0.3)",
                    }}
                    onMouseEnter={(e) => { if (waitlistStatus !== "submitting") { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(11, 99, 224, 0.4)"; } }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(11, 99, 224, 0.3)"; }}
                  >
                    {waitlistStatus === "submitting" ? "Submitting…" : "Join the Waitlist"}
                    <Shine />
                  </button>
                </form>
              </>
            )}
          </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
